/**
 * Dialog für die zwei in Phase 2 freigeschalteten Support-Aktionen:
 * Patient-Stammdaten ändern und Rechnungs-Status korrigieren. Wird vom
 * `SupportModeBanner` per Dropdown geöffnet und reicht die Eingaben an
 * die RPCs in `data/supportActions.ts` durch.
 *
 * Bewusst minimal gehalten:
 *   • Input für Ziel-ID (Patient/Rechnung) statt eines komplexen Such-
 *     Pickers — die Phase-2-UI ist Proof-of-Concept; die häufigsten
 *     Einstiegspunkte (PatientList-Row-Action, Invoice-Detail-Page)
 *     ziehen in Folge-PRs ein, sobald die Validierungslogik live ist.
 *   • Reason-Feld ist immer Pflicht und wird vom Server gegengeprüft.
 *   • Bei Erfolg zeigt eine Toast die `support_action.id` an, damit der
 *     Admin den Eintrag im DSGVO-Audit später wiederfindet.
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  supportUpdatePatient,
  supportUpdateInvoiceStatus,
  supportUndeletePatient,
  supportUpdateAppointmentStatus,
} from '@/data/supportActions';

export type ActionType = 'patient' | 'invoice' | 'undelete' | 'appointment';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultAction?: ActionType;
  /** Wenn gesetzt, wird die Patient/Invoice/Appointment-ID vorausgefüllt
   *  und das ID-Input nur read-only angezeigt. Wird vom Inline-Trigger
   *  aus den Detail-Seiten genutzt, damit der Admin die UUID nicht
   *  abtippen muss. */
  defaultId?: string;
  /** Wenn `true` und `defaultId` gesetzt: keine Action-Auswahl, kein
   *  ID-Editieren — der Dialog ist im "kontextgebundenen" Modus
   *  geöffnet. */
  contextLocked?: boolean;
}

const INVOICE_STATUSES = ['draft', 'open', 'paid', 'overdue', 'cancelled'] as const;
type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

const APPOINTMENT_STATUSES = ['scheduled', 'cancelled', 'no_show', 'completed'] as const;
type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

export function SupportActionDialog({
  open,
  onOpenChange,
  defaultAction = 'patient',
  defaultId,
  contextLocked = false,
}: Props) {
  const [action, setAction] = useState<ActionType>(defaultAction);
  const [submitting, setSubmitting] = useState(false);

  // Patient-Form-State (für sowohl "patient" als auch "undelete" verwendet)
  const [patientId, setPatientId] = useState(
    defaultAction === 'patient' || defaultAction === 'undelete' ? defaultId ?? '' : '',
  );
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  // Invoice-Form-State
  const [invoiceId, setInvoiceId] = useState(defaultAction === 'invoice' ? defaultId ?? '' : '');
  const [invoiceStatus, setInvoiceStatus] = useState<InvoiceStatus>('paid');

  // Appointment-Form-State
  const [appointmentId, setAppointmentId] = useState(
    defaultAction === 'appointment' ? defaultId ?? '' : '',
  );
  const [appointmentStatus, setAppointmentStatus] = useState<AppointmentStatus>('cancelled');

  const [reason, setReason] = useState('');

  // Wenn der Dialog mit einem neuen Default geöffnet wird (z. B. der
  // Inline-Trigger schaltet zwischen zwei Patienten hin und her),
  // synchen wir die ID-Felder. Ohne das blieben sie auf dem ersten Wert.
  useEffect(() => {
    if (!open || !defaultId) return;
    setAction(defaultAction);
    if (defaultAction === 'patient' || defaultAction === 'undelete') setPatientId(defaultId);
    if (defaultAction === 'invoice') setInvoiceId(defaultId);
    if (defaultAction === 'appointment') setAppointmentId(defaultId);
  }, [open, defaultId, defaultAction]);

  const reset = () => {
    setPatientId('');
    setEmail('');
    setPhone('');
    setNotes('');
    setInvoiceId('');
    setInvoiceStatus('paid');
    setAppointmentId('');
    setAppointmentStatus('cancelled');
    setReason('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      let actionId: string;
      let toastMessage: string;

      if (action === 'patient') {
        // Mindestens ein Feld muss gesetzt sein — die Server-RPC würde
        // das auch ablehnen, aber wir fangen es client-seitig ab, damit
        // der Admin keinen umsonst-Roundtrip provoziert.
        if (!email && !phone && !notes) {
          toast.error('Mindestens eines von E-Mail, Telefon oder Notizen ausfüllen.');
          setSubmitting(false);
          return;
        }
        actionId = await supportUpdatePatient(
          patientId.trim(),
          {
            email: email.trim() || null,
            phone: phone.trim() || null,
            notes: notes.trim() || null,
          },
          reason.trim(),
        );
        toastMessage = 'Patient korrigiert.';
      } else if (action === 'undelete') {
        actionId = await supportUndeletePatient(patientId.trim(), reason.trim());
        toastMessage = 'Patient wiederhergestellt.';
      } else if (action === 'appointment') {
        actionId = await supportUpdateAppointmentStatus(
          appointmentId.trim(),
          appointmentStatus,
          reason.trim(),
        );
        toastMessage = 'Termin-Status gesetzt.';
      } else {
        actionId = await supportUpdateInvoiceStatus(
          invoiceId.trim(),
          invoiceStatus,
          reason.trim(),
        );
        toastMessage = 'Rechnungs-Status gesetzt.';
      }
      toast.success(`${toastMessage} Audit-ID: ${actionId.slice(0, 8)}…`);
      reset();
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Aktion fehlgeschlagen.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Support-Aktion</DialogTitle>
          <DialogDescription>
            Auditierte Schreibaktion in der aktiven Support-Session. Jede Änderung
            wird in <code>audit_logs</code> und <code>support_actions</code> protokolliert.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Wenn der Dialog kontextgebunden geöffnet wurde (z. B. aus
              der Patientenakte), ist die Aktion implizit klar und der
              Action-Picker entfällt. Das hält den UX-Lärm klein und
              verhindert, dass der Admin versehentlich auf einen
              anderen Workflow umschaltet. */}
          {!contextLocked && (
            <div className="space-y-1.5">
              <Label htmlFor="support-action-type">Aktion</Label>
              <Select value={action} onValueChange={(v) => setAction(v as ActionType)}>
                <SelectTrigger id="support-action-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="patient">Patienten-Stammdaten ändern</SelectItem>
                  <SelectItem value="undelete">Patient wiederherstellen</SelectItem>
                  <SelectItem value="appointment">Termin-Status setzen</SelectItem>
                  <SelectItem value="invoice">Rechnungs-Status setzen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {action === 'patient' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="support-patient-id">Patient-ID (UUID)</Label>
                <Input
                  id="support-patient-id"
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                  required
                  readOnly={contextLocked}
                  className={contextLocked ? 'bg-muted font-mono text-xs' : undefined}
                  placeholder="z. B. 7e93…"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="support-patient-email">E-Mail (optional)</Label>
                <Input
                  id="support-patient-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="leer = unverändert"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="support-patient-phone">Telefon (optional)</Label>
                <Input
                  id="support-patient-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="leer = unverändert"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="support-patient-notes">Notizen (optional)</Label>
                <Textarea
                  id="support-patient-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="leer = unverändert"
                />
              </div>
            </>
          )}

          {action === 'undelete' && (
            <div className="space-y-1.5">
              <Label htmlFor="support-undelete-id">Patient-ID (UUID)</Label>
              <Input
                id="support-undelete-id"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                required
                readOnly={contextLocked}
                className={contextLocked ? 'bg-muted font-mono text-xs' : undefined}
                placeholder="z. B. 7e93…"
              />
              <p className="text-xs text-muted-foreground">
                Setzt <code>is_deleted=false</code>, <code>deleted_at=NULL</code>.
                Schlägt fehl, wenn der Patient gar nicht gelöscht ist.
              </p>
            </div>
          )}

          {action === 'appointment' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="support-appointment-id">Termin-ID (UUID)</Label>
                <Input
                  id="support-appointment-id"
                  value={appointmentId}
                  onChange={(e) => setAppointmentId(e.target.value)}
                  required
                  readOnly={contextLocked}
                  className={contextLocked ? 'bg-muted font-mono text-xs' : undefined}
                  placeholder="z. B. a82c…"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="support-appointment-status">Neuer Status</Label>
                <Select
                  value={appointmentStatus}
                  onValueChange={(v) => setAppointmentStatus(v as AppointmentStatus)}
                >
                  <SelectTrigger id="support-appointment-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {APPOINTMENT_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {action === 'invoice' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="support-invoice-id">Rechnungs-ID (UUID)</Label>
                <Input
                  id="support-invoice-id"
                  value={invoiceId}
                  onChange={(e) => setInvoiceId(e.target.value)}
                  required
                  readOnly={contextLocked}
                  className={contextLocked ? 'bg-muted font-mono text-xs' : undefined}
                  placeholder="z. B. b3a1…"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="support-invoice-status">Neuer Status</Label>
                <Select
                  value={invoiceStatus}
                  onValueChange={(v) => setInvoiceStatus(v as InvoiceStatus)}
                >
                  <SelectTrigger id="support-invoice-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INVOICE_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="support-reason">
              Grund <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="support-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              rows={2}
              placeholder="z. B. Ticket #1234 — Kunde hat um Korrektur der E-Mail gebeten."
            />
            <p className="text-xs text-muted-foreground">
              Wird in <code>audit_logs</code> gespeichert und ist für den Kunden-Admin sichtbar.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Aktion ausführen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
